import { HTMLAttributes, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useUpdatePickupPerson } from 'typescript/hooks/use_bopis_client';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { QUERY_KEYS } from 'typescript/constants/react_query';
import { isOrderFree } from 'typescript/utilities/order_data';
import DeliveryPageCTA from 'typescript/apps/checkout_v3/components/delivery/delivery_page_cta';
import PickupOptionsFinder from 'typescript/apps/checkout_v3/components/delivery/bopis/pickup_options_finder';
import PickupPersonInfoForm from 'typescript/apps/checkout_v3/components/delivery/bopis/pickup_person_info_form';
import RecaptchaModal from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal';
import usePickupPersonForm from 'typescript/apps/checkout_v3/components/delivery/bopis/use_pickup_person_form';
import {
  useInvalidateQuery,
  useIsProcessingAnyRequest,
} from 'typescript/hooks/use_react_query_utils';
import {
  onCheckoutPageClick,
  onCheckoutPageViewCombined,
} from 'typescript/utilities/tracking';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';

const PickUpInStore = ({ className }: HTMLAttributes<HTMLDivElement>) => {
  const { countryCode } = useLocaleInfo();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { isProcessing } = useIsProcessingAnyRequest();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const { updatePickupPerson } = useUpdatePickupPerson({ orderNumber });
  const { invalidate } = useInvalidateQuery();

  // user may previously have selected a location, preselect it
  const initialReservedLocationId = orderSummaryData?.bopis_fulfillment_selected
    ? orderSummaryData?.pickup_location_info?.location_id
    : '';

  // to render user selections before the API returns
  const [chosenPickupLocationId, setChosenPickupLocationId] = useState(
    initialReservedLocationId,
  );

  const { formMethods } = usePickupPersonForm({ countryCode });
  const {
    control,
    formState: { isValid, isDirty, touchedFields },
    getValues: getPickupPersonValues,
    setValue: setPickupPersonValue,
    reset: resetPickupPersonForm,
    handleSubmit,
  } = formMethods;

  const handleSubmitWithLogging = (callback) => (event) => {
    onCheckoutPageClick({
      eventDesc: isOrderFree(orderSummaryData?.payment_due)
        ? CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_complete_order
        : CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_continue_to_payment,
      trackingData,
    });

    // ensure we refresh data so pickup person is pulled
    invalidate(QUERY_KEYS.ORDER_SUMMARY, { orderNumber });

    return handleSubmit(callback)(event);
  };

  const updatePickupPersonInfo = () => {
    const pickupPersonFields = getPickupPersonValues();
    updatePickupPerson(
      {
        pickupPersonInfo: {
          first_name: pickupPersonFields.pickup_first_name,
          last_name: pickupPersonFields.pickup_last_name,
          email: pickupPersonFields.pickup_email,
          phone: pickupPersonFields.pickup_phone,
        },
      },
      {
        onSettled: () => {
          resetPickupPersonForm(pickupPersonFields);
        },
      },
    );
  };

  // pre-fill pickup person from order
  useEffect(() => {
    const { pickup_person_info: pickupPersonInfo } = orderSummaryData || {};

    Object.entries({ ...pickupPersonInfo }).forEach(([key, value]) => {
      setPickupPersonValue(`pickup_${key}`, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    });
  }, []);

  // update pickup person on order when changed if valid
  const isAnyFieldTouched = Object.keys(touchedFields).length > 0;
  useEffect(() => {
    if (isValid && isAnyFieldTouched && isDirty) {
      updatePickupPersonInfo();
    }
  }, [isValid, isAnyFieldTouched, isDirty]);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.PICK_UP_IN_STORE,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <div className="shop-grid-item-full shop-grid-container">
      <PickupOptionsFinder
        chosenPickupLocationId={chosenPickupLocationId}
        setChosenPickupLocationId={setChosenPickupLocationId}
      />
      <FormProvider {...formMethods}>
        <form
          className={`shop-grid-container ${className}`}
          onSubmit={(e) => e.preventDefault()}
        >
          <RecaptchaModal handleFormSubmit={handleSubmitWithLogging} />
          <PickupPersonInfoForm />
          <DeliveryPageCTA
            className="shop-grid-item-full"
            control={control}
            canContinue={
              isValid &&
              Boolean(chosenPickupLocationId) &&
              orderSummaryData.bopis_fulfillment_selected &&
              !isProcessing
            }
            isLoading={isProcessing}
            handleSubmit={handleSubmitWithLogging}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default PickUpInStore;
